import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { NavLink } from './nav-link';

import css from './detail-layout.module.css';

type Props = {
  crumbs: { label: string; href: string }[];
  imageUrl: string | null;
  imageAlt: string;
  category: string;
  title: string;
  /** Label/value rows (duration, rating, price…). */
  facts: { label: string; value: string }[];
  /** The CTA block — or the "no longer available" notice. */
  action: ReactNode;
  /** Desktop: the title/facts/CTA column stays in view beside the description. */
  stickyPanel?: boolean;
  /** A 16:9 frame instead of 3:2 — for images that are banners with text
   *  (Platinumlist's event images are 960×540), which a 3:2 crop cuts off. */
  wideImage?: boolean;
  children: ReactNode;
};

// A tour or boat page: breadcrumbs, then the photo beside the title, facts
// and booking block (stacked on phones), then the description.
export function DetailLayout({
  crumbs,
  imageUrl,
  imageAlt,
  category,
  title,
  facts,
  action,
  stickyPanel,
  wideImage,
  children,
}: Props) {
  const imageClass = wideImage ? `${css.image} ${css.wide}` : css.image;
  const { t } = useTranslation();
  return (
    <article className={css.wrap}>
      <nav aria-label={t('a11y.breadcrumb')} className={css.crumbs}>
        {crumbs.map((c) => (
          <NavLink key={c.href} href={c.href} className={css.crumb}>
            {c.label}
          </NavLink>
        ))}
      </nav>
      <div className={stickyPanel ? `${css.top} ${css.sticky}` : css.top}>
        {imageUrl ? (
          // Above the fold — the LCP element: a raw eager <img> (root AGENTS.md).
          <img className={imageClass} src={imageUrl} alt={imageAlt} fetchPriority="high" />
        ) : (
          <div className={`${imageClass} ${css.noImage}`} aria-hidden="true" />
        )}
        <div className={css.panel}>
          <span className={css.category}>{category}</span>
          <h1 className={css.title}>{title}</h1>
          {facts.length ? (
            <dl className={css.facts}>
              {facts.map((f) => (
                <div key={f.label} className={css.fact}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          <div className={css.action}>{action}</div>
        </div>
        <div className={css.body}>{children}</div>
      </div>
    </article>
  );
}
